import {
    ArrowRight,
    BadgeCheck,
    Building2,
    CheckCircle2,
    ChevronRight,
    Clock3,
    FileText,
    Info,
    MapPin,
    MessageCircle,
    PackageCheck,
    ReceiptText,
    Search,
    Share2,
    ShieldCheck,
    ShoppingBag,
    Star,
    Truck,
} from "lucide-react";
import {
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    useNavigate,
    useParams,
} from "react-router-dom";

import {
    getSellerProfile,
} from "./SellerInfoData";
import {
    SellerFeedback,
    SellerFeedbackPeriod,
    SellerFeedbackTone,
} from "./SellerInfoTypes";
import {
    getSellerFeedbackSummary,
    getSellerReviews,
} from "../../../Redux/storeReview/slice";
import {
    sellerFeedbackReasons,
} from "../SellerFeedbackPage/SellerFeedbackData";
import {
    SellerReview,
} from "../../../Redux/storeReview/types";
import {
    useAppDispatch,
    useAppSelector,
} from "../../../Redux/hooks";
import {
    RootState,
} from "../../../store";
import "./SellerInfoPage.css";
import { useCart } from "../../../context/CartContext";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import MobileDetailNavbar from "../../globalcomponents/mobile/MobileDetailNavbar/MobileDetailNavbar";
import HomeSecondaryNavbar from "../../globalcomponents/HomeSecondaryNavbar/HomeSecondaryNavbar";

const periods: {
    id: SellerFeedbackPeriod;
    label: string;
}[] = [
        {
            id: "30-days",
            label: "30 days",
        },
        {
            id: "90-days",
            label: "90 days",
        },
        {
            id: "12-months",
            label: "12 months",
        },
        {
            id: "lifetime",
            label: "Lifetime",
        },
    ];

const filters: {
    id:
    | "all"
    | SellerFeedbackTone;
    label: string;
}[] = [
        {
            id: "all",
            label: "All",
        },
        {
            id: "positive",
            label: "Positive",
        },
        {
            id: "neutral",
            label: "Neutral",
        },
        {
            id: "negative",
            label: "Negative",
        },
    ];

const formatNumber = (
    value: number
) =>
    new Intl.NumberFormat(
        "en-IN"
    ).format(value);

const formatPrice = (
    value: number
) =>
    new Intl.NumberFormat(
        "en-IN"
    ).format(value);

const SellerInfoPage = () => {
    const navigate =
        useNavigate();

    const dispatch =
        useAppDispatch();

    const {
        cartItems,
    } = useCart();

    const cartCount =
        cartItems.reduce(
            (
                total,
                cartItem
            ) =>
                total +
                cartItem.quantity,
            0
        );

    const {
        sellerSlug =
        "salar-retail",
    } =
        useParams();

    const seller =
        useMemo(
            () =>
                getSellerProfile(
                    sellerSlug
                ),
            [
                sellerSlug,
            ]
        );

    const [
        browsingHistoryOpen,
        setBrowsingHistoryOpen,
    ] =
        useState(false);

    const [
        feedbackPeriod,
        setFeedbackPeriod,
    ] =
        useState<SellerFeedbackPeriod>(
            "12-months"
        );

    const [
        feedbackFilter,
        setFeedbackFilter,
    ] =
        useState<
            | "all"
            | SellerFeedbackTone
        >(
            "all"
        );

    const [
        feedbackSearch,
        setFeedbackSearch,
    ] =
        useState("");

    const [
        copied,
        setCopied,
    ] =
        useState(false);

    const {
        sellerReviews,
        sellerStats,
        sellerSummary,
        sellerReviewsMeta,
    } =
        useAppSelector(
            (
                state: RootState
            ) => state.storeReview
        );

    const [
        feedbackLimit,
        setFeedbackLimit,
    ] =
        useState(10);

    const apiToneFilter =
        feedbackFilter ===
            "all"
            ? undefined
            : feedbackFilter ===
                "neutral"
                ? "3"
                : feedbackFilter;

    const hasApiFeedback =
        Boolean(
            sellerSummary ||
            sellerStats
        );

    useEffect(
        () => {
            if (
                sellerSlug
            ) {
                dispatch(
                    getSellerFeedbackSummary(
                        sellerSlug
                    )
                );
            }
        },
        [
            dispatch,
            sellerSlug,
        ]
    );

    useEffect(
        () => {
            if (
                sellerSlug
            ) {
                dispatch(
                    getSellerReviews(
                        {
                            sellerId:
                                sellerSlug,
                            page: 1,
                            limit:
                                feedbackLimit,
                            filter:
                                apiToneFilter,
                            search:
                                feedbackSearch
                                    .trim() ||
                                undefined,
                        }
                    )
                );
            }
        },
        [
            dispatch,
            sellerSlug,
            feedbackLimit,
            apiToneFilter,
            feedbackSearch,
        ]
    );

    const apiFeedback: SellerFeedback[] =
        useMemo(
            () =>
                sellerReviews.map(
                    (
                        review: SellerReview
                    ) => ({
                        id: review._id,
                        buyer:
                            review.customerId
                                ?.name ||
                            "Salar customer",
                        rating:
                            review.rating,
                        tone:
                            review.rating >=
                                4
                                ? "positive"
                                : review.rating ===
                                    3
                                    ? "neutral"
                                    : "negative",
                        title:
                            review
                                .reasons
                                ?.length
                                ? sellerFeedbackReasons.find(
                                    (
                                        reason
                                    ) =>
                                        reason.id ===
                                        review
                                            .reasons?.[0]
                                )?.label ||
                                "Seller feedback"
                                : "Seller feedback",
                        comment:
                            review.review ||
                            "",
                        date:
                            review.createdAt
                                ? new Date(
                                    review.createdAt
                                ).toLocaleDateString(
                                    "en-IN",
                                    {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric",
                                    }
                                )
                                : "",
                        orderType:
                            review.orderId
                                ? `Order ${review.orderId}`
                                : "Verified order",
                    })
                ),
            [
                sellerReviews,
            ]
        );

    const summary =
        useMemo(
            () => {
                if (
                    sellerSummary
                ) {
                    const bucket =
                        sellerSummary[
                        feedbackPeriod
                        ];
                    const pct = (
                        count: number
                    ) =>
                        bucket.total
                            ? Math.round(
                                (count /
                                    bucket.total) *
                                100
                            )
                            : 0;
                    return {
                        positive:
                            pct(
                                bucket.positive
                            ),
                        neutral:
                            pct(
                                bucket.neutral
                            ),
                        negative:
                            pct(
                                bucket.negative
                            ),
                        total:
                            bucket.total,
                    };
                }
                return seller
                    .feedbackSummary[
                feedbackPeriod
                ];
            },
            [
                sellerSummary,
                feedbackPeriod,
                seller.feedbackSummary,
            ]
        );

    const visibleFeedback =
        useMemo(
            () => {
                if (
                    hasApiFeedback
                ) {
                    return apiFeedback;
                }
                const query =
                    feedbackSearch
                        .trim()
                        .toLowerCase();

                return seller.feedback.filter(
                    (
                        item
                    ) => {
                        const toneMatch =
                            feedbackFilter ===
                            "all" ||
                            item.tone ===
                            feedbackFilter;

                        const searchMatch =
                            !query ||
                            item.title
                                .toLowerCase()
                                .includes(
                                    query
                                ) ||
                            item.comment
                                .toLowerCase()
                                .includes(
                                    query
                                ) ||
                            item.orderType
                                .toLowerCase()
                                .includes(
                                    query
                                );

                        return (
                            toneMatch &&
                            searchMatch
                        );
                    }
                );
            },
            [
                hasApiFeedback,
                apiFeedback,
                feedbackFilter,
                feedbackSearch,
                seller.feedback,
            ]
        );

    const handleShare =
        async () => {
            const shareData = {
                title:
                    `${seller.displayName} on Salar`,
                text:
                    `View seller details for ${seller.displayName}.`,
                url:
                    window.location.href,
            };

            try {
                if (
                    navigator.share
                ) {
                    await navigator.share(
                        shareData
                    );

                    return;
                }

                await navigator.clipboard.writeText(
                    window.location.href
                );

                setCopied(
                    true
                );

                window.setTimeout(
                    () =>
                        setCopied(
                            false
                        ),
                    1500
                );
            } catch {
                setCopied(
                    false
                );
            }
        };

    const scrollTo = (
        id: string
    ) => {
        document
            .getElementById(
                id
            )
            ?.scrollIntoView({
                behavior:
                    "smooth",
                block:
                    "start",
            });
    };

    return (
        <>
            <div className="seller-info-desktop-navbar">
                <HomeNavbar
                    variant="detail"
                />
            </div>

            <div className="seller-info-mobile-navbar">
                <MobileDetailNavbar
                    cartCount={
                        cartCount
                    }
                    searchPlaceholder={`Search ${seller.displayName}`}
                    fallbackPath="/HomePage"
                />
            </div>

            <div className="seller-info-desktop-secondary-navbar">
                <HomeSecondaryNavbar
                    variant="detail"
                    browsingHistoryOpen={
                        browsingHistoryOpen
                    }
                    onBrowsingHistoryClick={() =>
                        setBrowsingHistoryOpen(
                            (
                                previous
                            ) =>
                                !previous
                        )
                    }
                />
            </div>

            <main className="seller-info-page">
                <div className="seller-info-shell">
                    <div className="seller-info-breadcrumb">
                        <button
                            type="button"
                            onClick={() =>
                                navigate(
                                    "/HomePage"
                                )
                            }
                        >
                            Home
                        </button>

                        <ChevronRight
                            size={13}
                        />

                        <span>
                            Seller profile
                        </span>

                        <ChevronRight
                            size={13}
                        />

                        <strong>
                            {
                                seller.displayName
                            }
                        </strong>
                    </div>

                    <section className="seller-info-top">
                        <div className="seller-info-profile-card">
                            <div className="seller-info-avatar">
                                {
                                    seller.initials
                                }
                            </div>

                            <div className="seller-info-profile-copy">
                                <div className="seller-info-name-row">
                                    <h1>
                                        {
                                            seller.displayName
                                        }
                                    </h1>

                                    {seller.verified && (
                                        <span>
                                            <BadgeCheck
                                                size={17}
                                            />

                                            Verified
                                        </span>
                                    )}
                                </div>

                                <p className="seller-info-status">
                                    {
                                        seller.status
                                    }
                                </p>

                                <p className="seller-info-about-short">
                                    {
                                        seller.about
                                    }
                                </p>

                                <div className="seller-info-meta">
                                    <span>
                                        <Clock3
                                            size={16}
                                        />
                                        Selling since{" "}
                                        {
                                            seller.sellerSince
                                        }
                                    </span>

                                    <span>
                                        <MapPin
                                            size={16}
                                        />
                                        {
                                            seller.shipsFrom
                                        }
                                    </span>

                                    <span>
                                        <PackageCheck
                                            size={16}
                                        />
                                        {
                                            seller.fulfilment
                                        }
                                    </span>
                                </div>

                                <div className="seller-info-actions">
                                    <button
                                        type="button"
                                        className="seller-info-primary"
                                        onClick={() =>
                                            scrollTo(
                                                "seller-contact"
                                            )
                                        }
                                    >
                                        <MessageCircle
                                            size={17}
                                        />
                                        Contact seller
                                    </button>

                                    {seller.brandStoreSlug && (
                                        <button
                                            type="button"
                                            className="seller-info-secondary"
                                            onClick={() =>
                                                navigate(
                                                    `/store/${seller.brandStoreSlug}`
                                                )
                                            }
                                        >
                                            Visit brand store
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        className="seller-info-secondary"
                                        onClick={
                                            handleShare
                                        }
                                    >
                                        <Share2
                                            size={17}
                                        />
                                        {copied
                                            ? "Copied"
                                            : "Share"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <aside className="seller-info-trust-card">
                            <div className="seller-info-positive">
                                <strong>
                                    {
                                        seller.positiveFeedback
                                    }
                                    %
                                </strong>

                                <span>
                                    positive seller feedback
                                </span>

                                <small>
                                    {
                                        formatNumber(
                                            seller.totalRatings
                                        )
                                    }{" "}
                                    ratings
                                </small>
                            </div>

                            <div className="seller-info-quick-stats">
                                <div>
                                    <ShoppingBag
                                        size={19}
                                    />

                                    <strong>
                                        {
                                            seller.ordersCompleted
                                        }
                                    </strong>

                                    <span>
                                        Orders
                                    </span>
                                </div>

                                <div>
                                    <Truck
                                        size={19}
                                    />

                                    <strong>
                                        {
                                            seller.onTimeDispatch
                                        }
                                    </strong>

                                    <span>
                                        On-time dispatch
                                    </span>
                                </div>

                                <div>
                                    <MessageCircle
                                        size={19}
                                    />

                                    <strong>
                                        {
                                            seller.responseTime
                                        }
                                    </strong>

                                    <span>
                                        Response
                                    </span>
                                </div>

                                <div>
                                    <ShieldCheck
                                        size={19}
                                    />

                                    <strong>
                                        {
                                            seller.cancellationRate
                                        }
                                    </strong>

                                    <span>
                                        Cancellation
                                    </span>
                                </div>
                            </div>
                        </aside>
                    </section>

                    <nav className="seller-info-nav">
                        <button
                            type="button"
                            onClick={() =>
                                scrollTo(
                                    "seller-feedback"
                                )
                            }
                        >
                            Seller feedback
                        </button>

                        <button
                            type="button"
                            onClick={() =>
                                scrollTo(
                                    "seller-about"
                                )
                            }
                        >
                            About seller
                        </button>

                        <button
                            type="button"
                            onClick={() =>
                                scrollTo(
                                    "seller-business"
                                )
                            }
                        >
                            Business details
                        </button>

                        <button
                            type="button"
                            onClick={() =>
                                scrollTo(
                                    "seller-contact"
                                )
                            }
                        >
                            Contact
                        </button>

                        <button
                            type="button"
                            onClick={() =>
                                scrollTo(
                                    "seller-policies"
                                )
                            }
                        >
                            Policies
                        </button>

                        <button
                            type="button"
                            onClick={() =>
                                scrollTo(
                                    "seller-products"
                                )
                            }
                        >
                            Products
                        </button>
                    </nav>

                    <section
                        className="seller-info-section seller-info-feedback"
                        id="seller-feedback"
                    >
                        <div className="seller-info-section-head">
                            <div>
                                <span>
                                    SELLER FEEDBACK
                                </span>

                                <h2>
                                    Seller rating & recent feedback
                                </h2>

                                <p>
                                    Feedback here is about the buying experience with the seller, not the product itself.
                                </p>
                            </div>
                        </div>

                        <div className="seller-info-feedback-overview">
                            <div className="seller-info-feedback-score">
                                <strong>
                                    {
                                        summary.positive
                                    }
                                    %
                                </strong>

                                <span>
                                    Positive
                                </span>

                                <small>
                                    {
                                        formatNumber(
                                            summary.total
                                        )
                                    }{" "}
                                    ratings
                                </small>
                            </div>

                            <div className="seller-info-feedback-breakdown">
                                <div className="seller-info-feedback-row">
                                    <span>
                                        Positive
                                    </span>

                                    <div className="seller-info-bar">
                                        <i
                                            style={{
                                                width:
                                                    `${summary.positive}%`,
                                            }}
                                        />
                                    </div>

                                    <strong>
                                        {
                                            summary.positive
                                        }
                                        %
                                    </strong>
                                </div>

                                <div className="seller-info-feedback-row">
                                    <span>
                                        Neutral
                                    </span>

                                    <div className="seller-info-bar">
                                        <i
                                            style={{
                                                width:
                                                    `${summary.neutral}%`,
                                            }}
                                        />
                                    </div>

                                    <strong>
                                        {
                                            summary.neutral
                                        }
                                        %
                                    </strong>
                                </div>

                                <div className="seller-info-feedback-row">
                                    <span>
                                        Negative
                                    </span>

                                    <div className="seller-info-bar">
                                        <i
                                            style={{
                                                width:
                                                    `${summary.negative}%`,
                                            }}
                                        />
                                    </div>

                                    <strong>
                                        {
                                            summary.negative
                                        }
                                        %
                                    </strong>
                                </div>
                            </div>

                            <div className="seller-info-periods">
                                {periods.map(
                                    (
                                        period
                                    ) => (
                                        <button
                                            type="button"
                                            key={
                                                period.id
                                            }
                                            className={
                                                feedbackPeriod ===
                                                    period.id
                                                    ? "active"
                                                    : ""
                                            }
                                            onClick={() =>
                                                setFeedbackPeriod(
                                                    period.id
                                                )
                                            }
                                        >
                                            {
                                                period.label
                                            }
                                        </button>
                                    )
                                )}
                            </div>
                        </div>

                        <div className="seller-info-feedback-toolbar">
                            <div className="seller-info-filters">
                                {filters.map(
                                    (
                                        filter
                                    ) => (
                                        <button
                                            type="button"
                                            key={
                                                filter.id
                                            }
                                            className={
                                                feedbackFilter ===
                                                    filter.id
                                                    ? "active"
                                                    : ""
                                            }
                                            onClick={() =>
                                                setFeedbackFilter(
                                                    filter.id
                                                )
                                            }
                                        >
                                            {
                                                filter.label
                                            }
                                        </button>
                                    )
                                )}
                            </div>

                            <label className="seller-info-search">
                                <Search
                                    size={17}
                                />

                                <input
                                    type="search"
                                    value={
                                        feedbackSearch
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setFeedbackSearch(
                                            event.target.value
                                        )
                                    }
                                    placeholder="Search feedback"
                                />
                            </label>
                        </div>

                        <div className="seller-info-feedback-list">
                            {visibleFeedback.map(
                                (
                                    item
                                ) => (
                                    <article
                                        className={`seller-info-feedback-card ${item.tone}`}
                                        key={
                                            item.id
                                        }
                                    >
                                        <div className="seller-info-feedback-card-head">
                                            <div>
                                                <strong>
                                                    {
                                                        item.buyer
                                                    }
                                                </strong>

                                                <span>
                                                    {
                                                        item.orderType
                                                    }
                                                </span>
                                            </div>

                                            <time>
                                                {
                                                    item.date
                                                }
                                            </time>
                                        </div>

                                        <div className="seller-info-stars">
                                            {[
                                                1,
                                                2,
                                                3,
                                                4,
                                                5,
                                            ].map(
                                                (
                                                    star
                                                ) => (
                                                    <Star
                                                        key={
                                                            star
                                                        }
                                                        size={15}
                                                        fill={
                                                            star <=
                                                                item.rating
                                                                ? "currentColor"
                                                                : "none"
                                                        }
                                                    />
                                                )
                                            )}
                                        </div>

                                        <h3>
                                            {
                                                item.title
                                            }
                                        </h3>

                                        <p>
                                            {
                                                item.comment
                                            }
                                        </p>

                                        {item.sellerResponse && (
                                            <div className="seller-info-response">
                                                <strong>
                                                    Seller response
                                                </strong>

                                                <p>
                                                    {
                                                        item.sellerResponse
                                                    }
                                                </p>
                                            </div>
                                        )}
                                    </article>
                                )
                            )}
                        </div>

                        {sellerReviewsMeta &&
                            sellerReviews.length <
                            sellerReviewsMeta.total && (
                                <div
                                    className="seller-info-periods"
                                    style={{
                                        justifyContent:
                                            "center",
                                        marginTop: 16,
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setFeedbackLimit(
                                                (
                                                    limit
                                                ) =>
                                                    limit +
                                                    10
                                            )
                                        }
                                    >
                                        Load more feedback
                                    </button>
                                </div>
                            )}
                    </section>

                    <section
                        className="seller-info-section"
                        id="seller-about"
                    >
                        <div className="seller-info-two-column">
                            <article className="seller-info-card">
                                <div className="seller-info-card-title">
                                    <ShieldCheck
                                        size={22}
                                    />

                                    <div>
                                        <span>
                                            ABOUT SELLER
                                        </span>

                                        <h2>
                                            About {
                                                seller.displayName
                                            }
                                        </h2>
                                    </div>
                                </div>

                                <p className="seller-info-long-copy">
                                    {
                                        seller.about
                                    }
                                </p>

                                <div className="seller-info-assurance">
                                    <CheckCircle2
                                        size={19}
                                    />

                                    <span>
                                        Verified marketplace seller identity and business information.
                                    </span>
                                </div>
                            </article>

                            <article
                                className="seller-info-card"
                                id="seller-business"
                            >
                                <div className="seller-info-card-title">
                                    <Building2
                                        size={22}
                                    />

                                    <div>
                                        <span>
                                            DETAILED SELLER INFORMATION
                                        </span>

                                        <h2>
                                            Business details
                                        </h2>
                                    </div>
                                </div>

                                <dl className="seller-info-business-list">
                                    {seller.businessInfo.map(
                                        (
                                            item
                                        ) => (
                                            <div
                                                key={
                                                    item.label
                                                }
                                            >
                                                <dt>
                                                    {
                                                        item.label
                                                    }
                                                </dt>

                                                <dd>
                                                    {
                                                        item.value
                                                    }
                                                </dd>
                                            </div>
                                        )
                                    )}
                                </dl>
                            </article>
                        </div>
                    </section>

                    <section
                        className="seller-info-section seller-info-contact"
                        id="seller-contact"
                    >
                        <div className="seller-info-section-head">
                            <div>
                                <span>
                                    CONTACT SELLER
                                </span>

                                <h2>
                                    Contact & support details
                                </h2>

                                <p>
                                    Use the order-linked support channel for the fastest and safest resolution.
                                </p>
                            </div>
                        </div>

                        <div className="seller-info-contact-grid">
                            <article>
                                <MessageCircle
                                    size={22}
                                />

                                <span>
                                    Primary support channel
                                </span>

                                <strong>
                                    {
                                        seller.supportChannel
                                    }
                                </strong>

                                <p>
                                    Best for delivery, return, replacement and order-specific questions.
                                </p>
                            </article>

                            <article>
                                <Clock3
                                    size={22}
                                />

                                <span>
                                    Support hours
                                </span>

                                <strong>
                                    {
                                        seller.supportHours
                                    }
                                </strong>

                                <p>
                                    Typical seller response: {
                                        seller.responseTime
                                    }.
                                </p>
                            </article>

                            <article>
                                <MapPin
                                    size={22}
                                />

                                <span>
                                    Registered address
                                </span>

                                <strong>
                                    {
                                        seller.businessAddress
                                    }
                                </strong>

                                <p>
                                    Public business information shown for marketplace transparency.
                                </p>
                            </article>

                            <article>
                                <ReceiptText
                                    size={22}
                                />

                                <span>
                                    Invoice support
                                </span>

                                <strong>
                                    {
                                        seller.invoiceSupport
                                    }
                                </strong>

                                <p>
                                    {
                                        seller.taxStatus
                                    }.
                                </p>
                            </article>

                            <article>
                                <Info
                                    size={22}
                                />

                                <span>
                                    Phone
                                </span>

                                <strong>
                                    {
                                        seller.supportPhone
                                    }
                                </strong>

                                <p>
                                    Direct personal seller numbers are not shown publicly.
                                </p>
                            </article>

                            <article>
                                <FileText
                                    size={22}
                                />

                                <span>
                                    Email
                                </span>

                                <strong>
                                    {
                                        seller.supportEmail
                                    }
                                </strong>

                                <p>
                                    Seller communication remains linked to your Salar account and order.
                                </p>
                            </article>
                        </div>

                        <div className="seller-info-contact-note">
                            <ShieldCheck
                                size={20}
                            />

                            <div>
                                <strong>
                                    Protected communication
                                </strong>

                                <p>
                                    Personal phone numbers and private email addresses are not exposed on this public seller profile. Use Salar Secure Messaging so communication stays attached to the order.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section
                        className="seller-info-section"
                        id="seller-policies"
                    >
                        <div className="seller-info-section-head">
                            <div>
                                <span>
                                    SELLER POLICIES
                                </span>

                                <h2>
                                    Shipping, returns & buyer support
                                </h2>
                            </div>
                        </div>

                        <div className="seller-info-policy-list">
                            {seller.policies.map(
                                (
                                    policy
                                ) => (
                                    <details
                                        key={
                                            policy.id
                                        }
                                        className="seller-info-policy"
                                    >
                                        <summary>
                                            <div>
                                                <FileText
                                                    size={19}
                                                />

                                                <span>
                                                    <strong>
                                                        {
                                                            policy.title
                                                        }
                                                    </strong>

                                                    <small>
                                                        {
                                                            policy.summary
                                                        }
                                                    </small>
                                                </span>
                                            </div>

                                            <ChevronRight
                                                size={18}
                                            />
                                        </summary>

                                        <ul>
                                            {policy.details.map(
                                                (
                                                    detail
                                                ) => (
                                                    <li
                                                        key={
                                                            detail
                                                        }
                                                    >
                                                        {
                                                            detail
                                                        }
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </details>
                                )
                            )}
                        </div>
                    </section>

                    <section
                        className="seller-info-section seller-info-products"
                        id="seller-products"
                    >
                        <div className="seller-info-products-head">
                            <div>
                                <span>
                                    PRODUCTS FROM THIS SELLER
                                </span>

                                <h2>
                                    Popular products
                                </h2>

                                <p>
                                    Browse a selection of items currently represented by this seller profile.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    navigate(
                                        "/category/men-fashion"
                                    )
                                }
                            >
                                View more

                                <ArrowRight
                                    size={16}
                                />
                            </button>
                        </div>

                        <div className="seller-info-product-grid">
                            {seller.products.map(
                                (
                                    product
                                ) => {
                                    const discount =
                                        Math.round(
                                            (
                                                (
                                                    product.mrp -
                                                    product.price
                                                ) /
                                                product.mrp
                                            ) *
                                            100
                                        );

                                    return (
                                        <article
                                            className="seller-info-product-card"
                                            key={
                                                product.id
                                            }
                                            role="button"
                                            tabIndex={0}
                                            onClick={() =>
                                                navigate(
                                                    `/product/${product.sourceId}`,
                                                    {
                                                        state: {
                                                            product: {
                                                                ...product,
                                                                id:
                                                                    product.sourceId,
                                                                productId:
                                                                    product.sourceId,
                                                                sourceId:
                                                                    product.sourceId,
                                                                seller:
                                                                    seller.displayName,
                                                                sellerSlug:
                                                                    seller.slug,
                                                                brand:
                                                                    "SALAR",
                                                            },
                                                        },
                                                    }
                                                )
                                            }
                                        >
                                            <div className="seller-info-product-image">
                                                <span>
                                                    {
                                                        product.badge
                                                    }
                                                </span>

                                                <img
                                                    src={
                                                        product.image
                                                    }
                                                    alt={
                                                        product.title
                                                    }
                                                />
                                            </div>

                                            <div className="seller-info-product-body">
                                                <small>
                                                    {
                                                        product.category
                                                    }
                                                </small>

                                                <h3>
                                                    {
                                                        product.title
                                                    }
                                                </h3>

                                                <p>
                                                    {
                                                        product.description
                                                    }
                                                </p>

                                                <div className="seller-info-product-rating">
                                                    <strong>
                                                        {
                                                            product.rating
                                                        }
                                                    </strong>

                                                    <Star
                                                        size={14}
                                                        fill="currentColor"
                                                    />

                                                    <span>
                                                        (
                                                        {
                                                            product.reviews
                                                        }
                                                        )
                                                    </span>
                                                </div>

                                                <div className="seller-info-product-price">
                                                    <strong>
                                                        ₹
                                                        {formatPrice(
                                                            product.price
                                                        )}
                                                    </strong>

                                                    <del>
                                                        ₹
                                                        {formatPrice(
                                                            product.mrp
                                                        )}
                                                    </del>

                                                    <span>
                                                        {
                                                            discount
                                                        }
                                                        % off
                                                    </span>
                                                </div>
                                            </div>
                                        </article>
                                    );
                                }
                            )}
                        </div>
                    </section>
                </div>
            </main>
        </>
    );
};

export default SellerInfoPage;
